import React from 'react';
import { GripVertical } from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

const THEME_COLOR = '#c59253';

const SectionOrdering = ({ state, updateState }) => {
    const spaces = state.spaces || [];
    const sections = state.sections || [];

    // Only show parent spaces
    const parentSpaces = spaces.filter(s => !s.parentId);

    const onDragEnd = (result, space) => {
        if (!result.destination) return;

        const selectedSectionIds = space.selectedSections || [];
        const currentOrder = (space.sectionOrder && space.sectionOrder.length > 0)
            ? [...space.sectionOrder]
            : [...selectedSectionIds];

        const [removed] = currentOrder.splice(result.source.index, 1);
        currentOrder.splice(result.destination.index, 0, removed);

        // Update state for this parent and its children
        const updatedSpaces = (state.spaces || []).map(s => {
            if (s.id === space.id || s.parentId === space.id) {
                return { ...s, sectionOrder: currentOrder };
            }
            return s;
        });

        updateState({ spaces: updatedSpaces });
    };

    return (
        <div className="h-100 overflow-auto">
            <div className="container py-5" style={{ maxWidth: '900px' }}>
                <div className="mb-5">
                    <h1 className="h3 mb-2">Section Ordering</h1>
                    <p className="text-muted">Arrange the order of menu sections for each space</p>
                    <div className="alert alert-info d-flex align-items-start gap-2 mt-3 text-dark bg-opacity-10" style={{ backgroundColor: `${THEME_COLOR}22`, borderColor: THEME_COLOR }}>
                        <div className="small">
                            <strong>Inheritance:</strong> Subspaces automatically inherit the section order from their parent space.
                        </div>
                    </div>
                </div>

                {parentSpaces.length === 0 && (
                    <div className="text-center py-5 bg-light border border-2 border-dashed rounded-3">
                        <p className="text-muted mb-0">No spaces created yet</p>
                    </div>
                )}

                <div className="d-flex flex-column gap-4">
                    {parentSpaces.map((space) => {
                        const selectedSectionIds = space.selectedSections || [];
                        const currentOrder = (space.sectionOrder && space.sectionOrder.length > 0)
                            ? space.sectionOrder
                            : selectedSectionIds;

                        const spaceSections = currentOrder
                            .map(sId => sections.find(s => s.id === sId))
                            .filter(Boolean);

                        const childSpaces = (state.spaces || []).filter(s => s.parentId === space.id);

                        return (
                            <div key={space.id} className="card border shadow-sm rounded-4 overflow-hidden">
                                <div className="card-body p-4">
                                    <div className="d-flex align-items-center justify-content-between mb-4">
                                        <div>
                                            <div className="d-flex align-items-center gap-2">
                                                <h5 className="mb-0 fw-bold">{space.name}</h5>
                                                {space.nameAr && <span className="text-muted small">({space.nameAr})</span>}
                                            </div>
                                            {childSpaces.length > 0 && (
                                                <p className="text-muted small mb-0 mt-1">
                                                    Linked to {childSpaces.length} subspace{childSpaces.length > 1 ? 's' : ''}
                                                </p>
                                            )}
                                        </div>
                                        <span className="badge rounded-pill bg-light text-dark px-3 py-2 border">
                                            {spaceSections.length} sections
                                        </span>
                                    </div>

                                    {spaceSections.length === 0 ? (
                                        <div className="text-center py-4 bg-light rounded-3 border border-dashed">
                                            <p className="text-muted small mb-0">No sections selected for this space</p>
                                        </div>
                                    ) : (
                                        <DragDropContext onDragEnd={(result) => onDragEnd(result, space)}>
                                            <Droppable droppableId={`droppable-${space.id}`}>
                                                {(provided) => (
                                                    <div
                                                        {...provided.droppableProps}
                                                        ref={provided.innerRef}
                                                        className="d-flex flex-column gap-2 p-2 rounded-3"
                                                        style={{ backgroundColor: '#f8f9fa' }}
                                                    >
                                                        {spaceSections.map((section, index) => (
                                                            <Draggable key={section.id} draggableId={section.id} index={index}>
                                                                {(provided, snapshot) => (
                                                                    <div
                                                                        ref={provided.innerRef}
                                                                        {...provided.draggableProps}
                                                                        {...provided.dragHandleProps}
                                                                        className={`d-flex align-items-center gap-3 p-3 bg-white border rounded-3 transition-all ${snapshot.isDragging ? 'shadow-lg border-warning' : ''}`}
                                                                        style={{
                                                                            ...provided.draggableProps.style,
                                                                            borderColor: snapshot.isDragging ? THEME_COLOR : undefined
                                                                        }}
                                                                    >
                                                                        <GripVertical size={20} className="text-muted" />
                                                                        <div className="flex-grow-1">
                                                                            <div className="d-flex align-items-center gap-3">
                                                                                <div
                                                                                    className="d-flex align-items-center justify-content-center bg-light text-muted fw-bold rounded-circle small"
                                                                                    style={{ width: '24px', height: '24px', fontSize: '11px' }}
                                                                                >
                                                                                    {index + 1}
                                                                                </div>
                                                                                <span className="fw-medium text-dark">{section.name}</span>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </Draggable>
                                                        ))}
                                                        {provided.placeholder}
                                                    </div>
                                                )}
                                            </Droppable>
                                        </DragDropContext>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default SectionOrdering;
